import { useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import type { RootState } from "../store/store";
import type {
  UserPermissions,
  PermissionCheckResult,
  PermissionsState,
  PermissionKey,
} from "../api/permissions/permissions.types";
import {
  PermissionCode,
  isPermissionGranted,
} from "../api/permissions/permissions.types";
import { setPermissions, resetPermissions } from "../store/PermissionsSlice";
import { features } from "../shared/constants/common.constant";
import type { KycFeatureSettings } from "../store/UserSlice";
import { useSnackbar } from "../shared/components/snackbar/Snackbar";

const selectPermissionsState = (state: RootState): PermissionsState =>
  state.permissions;

export const selectIsAuthorizationFeatureEnabled = (
  state: RootState
): boolean =>
  Boolean(
    state.user?.kycFeatures?.some(
      (feature: KycFeatureSettings) =>
        feature[features.enableAuthorization] === "on"
    )
  );

export const selectPermissions = createSelector(
  selectPermissionsState,
  (permissionsState) => permissionsState.permissions
);

export const selectPermission = createSelector(
  [selectPermissions, (_state: RootState, code: string) => code],
  (permissions, code) => isPermissionGranted(permissions, code)
);

export const selectIsPermissionsLoading = createSelector(
  selectPermissionsState,
  (permissionsState) => permissionsState.isLoading
);

export const selectIsPermissionsLoaded = createSelector(
  selectPermissionsState,
  (permissionsState) => permissionsState.isLoaded
);

export const selectPermissionsError = createSelector(
  selectPermissionsState,
  (permissionsState) => permissionsState.error
);

export const usePermissions = (): UserPermissions => {
  return useSelector(selectPermissions);
};

export const useIsPermissionsLoading = (): boolean => {
  return useSelector(selectIsPermissionsLoading);
};

export const useIsPermissionsLoaded = (): boolean => {
  return useSelector(selectIsPermissionsLoaded);
};

export const usePermissionsError = (): string | null => {
  return useSelector(selectPermissionsError);
};

export const useHasPermission = (code: PermissionKey): boolean => {
  const key = String(code);
  const authorizationEnabled = useSelector(selectIsAuthorizationFeatureEnabled);
  const granted = useSelector((state: RootState) =>
    selectPermission(state, key)
  );
  return !authorizationEnabled || granted;
};

export const usePermissionCheck = (code: string): PermissionCheckResult => {
  const permissions = usePermissions();
  const isLoading = useIsPermissionsLoading();
  const error = usePermissionsError();
  const { showSnackbar } = useSnackbar();
  const { t: translate } = useTranslation(["common"]);

  useEffect(() => {
    if (error) {
      showSnackbar(translate("common:errorOccurredPleaseTryAgain"), true);
    }
  }, [error, showSnackbar, translate]);

  return useMemo(
    () => ({
      hasPermission: isPermissionGranted(permissions, code),
      isLoading,
      error,
    }),
    [permissions, code, isLoading, error]
  );
};

export const useMultiplePermissions = (
  codes: string[]
): Record<string, boolean> => {
  const permissions = usePermissions();

  return useMemo(() => {
    const result: Record<string, boolean> = {};
    codes.forEach((c) => {
      result[c] = isPermissionGranted(permissions, c);
    });
    return result;
  }, [permissions, codes]);
};

export const useHasAllPermissions = (codes: string[]): boolean => {
  const permissions = usePermissions();

  return useMemo(
    () => codes.every((code) => isPermissionGranted(permissions, code)),
    [permissions, codes]
  );
};

export const useHasAnyPermission = (codes: string[]): boolean => {
  const permissions = usePermissions();

  return useMemo(
    () => codes.some((code) => isPermissionGranted(permissions, code)),
    [permissions, codes]
  );
};

export const useUpdatePermissions = (): ((
  _permissions: UserPermissions
) => void) => {
  const dispatch = useDispatch();
  const teamId = useSelector(
    (state: RootState) => state.permissions.teamId
  );
  return useCallback(
    (newPermissions: UserPermissions) => {
      dispatch(setPermissions({ permissions: newPermissions, teamId }));
    },
    [dispatch, teamId]
  );
};

export const useResetPermissions = (): (() => void) => {
  const dispatch = useDispatch();
  return useCallback(() => {
    dispatch(resetPermissions());
  }, [dispatch]);
};

export const usePermissionMap = (): Record<PermissionCode, boolean> => {
  const isAuthorizationEnabled = useSelector(
    selectIsAuthorizationFeatureEnabled
  );
  const permissions = usePermissions();

  return useMemo(() => {
    const permissionMap = {} as Record<PermissionCode, boolean>;

    for (const code of Object.values(PermissionCode)) {
      permissionMap[code] = isAuthorizationEnabled
        ? isPermissionGranted(permissions, code)
        : true;
    }

    return permissionMap;
  }, [isAuthorizationEnabled, permissions]);
};
